import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Box, Button, IconButton, InputAdornment, TextField, Tooltip, alpha } from '@mui/material';
import { Eye, Filter, Plus, Search, X } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import { Permission } from '../../../../utils/helpers/permissions';
import PermissionGate from '../../../../utils/hooks/PermissionGate';

export interface JobOpeningsControlsProps {
  searchJobTitle: string;
  onSearchChange: (value: string) => void;
  activeTab: string;
  onOpenFilter: () => void;
  activeFilterCount: number;
  onCreateClick: () => void;
  hasAnyLicense: boolean;
}

export const JobOpeningsControls = ({
  searchJobTitle,
  onSearchChange,
  activeTab,
  onOpenFilter,
  activeFilterCount,
  onCreateClick,
  hasAnyLicense,
}: JobOpeningsControlsProps) => {
  const { t } = useTranslation(NS.adminPages);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 1.5,
        width: { xs: '100%', xl: 'auto' },
      }}
    >
      {/* Search Input */}
      <TextField
        size="small"
        placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.list.searchPlaceholder, { tab: activeTab })}
        value={searchJobTitle}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} color="#94a3b8" />
            </InputAdornment>
          ),
          endAdornment: searchJobTitle ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onSearchChange('')} aria-label="Clear search">
                <X size={14} />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: {
            borderRadius: 2,
            bgcolor: 'background.paper',
            fontSize: '0.8125rem',
            '& fieldset': {
              borderColor: 'grey.300',
            },
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
        sx={{ minWidth: { xs: '100%', sm: 260, xl: 280 } }}
      />

      {/* Filter Button */}
      <Badge
        badgeContent={activeFilterCount}
        color="primary"
        sx={{
          '& .MuiBadge-badge': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 700,
            fontSize: '0.7rem',
          },
        }}
      >
        <Button
          variant="outlined"
          size="medium"
          onClick={onOpenFilter}
          startIcon={<Filter size={16} />}
          sx={{
            height: 40,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 600,
            borderColor: activeFilterCount > 0 ? 'primary.main' : 'grey.300',
            color: activeFilterCount > 0 ? 'primary.main' : 'text.primary',
            bgcolor: (theme) => (activeFilterCount > 0 ? alpha(theme.palette.primary.main, 0.08) : 'background.paper'),
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          {t(ADMIN_PAGES_KEYS.jobOpenings.list.filter)}
        </Button>
      </Badge>

      {/* Public Preview Button */}
      <Button
        component={Link}
        to="/jobs"
        target="_blank"
        rel="noopener noreferrer"
        variant="outlined"
        size="medium"
        startIcon={<Eye size={16} />}
        sx={{
          height: 40,
          px: 2,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 600,
          borderColor: 'grey.300',
          color: 'text.primary',
          bgcolor: 'background.paper',
          whiteSpace: 'nowrap',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          },
        }}
      >
        Public Preview
      </Button>

      {/* Create New Job Opening Button */}
      <PermissionGate
        requiredPermissions={[Permission.ASSESSMENTS_CREATE, Permission.QUESTIONS_VIEW]}
        matchAnyPermission={false}
      >
        {hasAnyLicense ? (
          <Button
            variant="contained"
            size="medium"
            onClick={onCreateClick}
            startIcon={<Plus size={16} />}
            sx={{
              height: 40,
              px: 2.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 600,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: '#ffffff',
              boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
              '&:hover': {
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.primary.main, 0.38)}`,
              },
            }}
          >
            {t(ADMIN_PAGES_KEYS.jobOpenings.list.createNew)}
          </Button>
        ) : (
          <Tooltip title={t(ADMIN_PAGES_KEYS.jobOpenings.list.noActiveLicenses)}>
            <span>
              <Button
                disabled
                variant="contained"
                size="medium"
                startIcon={<Plus size={16} />}
                sx={{
                  height: 40,
                  px: 2.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                }}
              >
                {t(ADMIN_PAGES_KEYS.jobOpenings.list.createNew)}
              </Button>
            </span>
          </Tooltip>
        )}
      </PermissionGate>
    </Box>
  );
};
